import { Box, Button, Card, Skeleton, Typography } from "@mui/material";
import { BubbleMenu, useEditor } from "@tiptap/react";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { AddPaymentModal } from "src/components/dashboard/addPaymenModal";
import { RegenerateDropdown } from "src/components/dashboard/brief/rightSection/aiArticle/regenerateDropdown";
import MenuBar from "src/components/dashboard/editor/MenuBar";
import { MagicWandIcon } from "src/icons/magic-wand-icon";
import { getActivePlan } from "src/slices/auth/thunks";

import Head from "next/head";
import {
  selectIsHumanized,
  setIsHumanized,
  setShowHumanizeContent,
} from "src/slices/humanize";
import { endHumanization } from "src/slices/humanize/thunks";
import { aiWordsUpgradeModalShowed, snackAdded } from "src/slices/system";
import { useDispatch, useSelector } from "src/store";
import { copyHtmlToClipboard, truncateContent, insertUnlockCardWithTipTap } from "src/utils/clipboardUtils";
import { Export2Docx } from "src/utils/docs";
import {
  addMetaToArticle,
  countSelectedWords,
  extractSelectedBlocks,
  getEditorExtensions,
  getMarkdownConverter,
  SelectedTextItem,
  setupTurndownService,
  useDebounce,
  getWordCount,
} from "src/utils/editorUtils";
import Editor from "../editor";
import { Onboarding } from "../onboarding";
import { OptimizeContentDialog } from "../optimizeContent/optimizeContentDialog";
import Sidebar from "./sidebar";

// Props for the ArticleEditorComponent
interface ArticleEditorComponentProps {
  id: string;
  title?: string;
  description?: string;
  isOptimizedMode?: boolean;
  hideArticle?: boolean;
  fetchContent: (id: string) => Promise<any>;
  saveContent: (content: string, options?: any) => Promise<any>;
  isDownloadContent?: boolean;
  isCopyContent?: boolean;
  additionalProps?: any;
  isEEATMode?: boolean;
}

const ArticleEditorComponent: React.FC<ArticleEditorComponentProps> = ({
  id,
  title = "Default Title",
  description = "Default Description",
  isOptimizedMode = false,
  hideArticle = false,
  fetchContent,
  saveContent,
  additionalProps = {},
  isEEATMode = false,
}) => {
  const dispatch = useDispatch();
  const isHumanized = useSelector(selectIsHumanized);
  const [showAIDropdown, setShowAIDropdown] = useState(false);
  const [selectedText, setSelectedText] = useState<SelectedTextItem[]>([]);
  const [shouldShowBubbleMenu, setShouldShowBubbleMenu] = useState(true);
  const [bottomMargin, setBottomMargin] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [isOptimizeContentDialogOpen, setIsOptimizeContentDialogOpen] =
    useState(false);
  const [cords, setCords] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  // Add state for selection coordinates to use in fixed positioning
  const [selectionCoords, setSelectionCoords] = useState({
    startCoords: { left: 0, right: 0, top: 0, bottom: 0 },
    endCoords: { left: 0, right: 0, top: 0, bottom: 0 },
  });
  const [html, setHtml] = useState("");
  const [humanizedHtml, setHumanizedHtml] = useState("");
  const [editorContent, setEditorContent] = useState<string>("");
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [isValidEvaluationData, setIsValidEvaluationData] =
    useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState("");
  const [isEditingHumanizedVersion, setIsEditingHumanizedVersion] =
    useState(false);
  const [isOptimizeContentDialogLoading, setIsOptimizeContentDialogLoading] =
    useState(false);
  const [articleLink, setArticleLink] = useState<string | null>(null);
  const [useScrollBasedTruncation, setUseScrollBasedTruncation] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const converter = getMarkdownConverter();
  const router = useRouter();

  // Refs to track programmatic changes and content saving
  const isContentSaving = React.useRef(false);
  const lastSavedContent = React.useRef("");
  const isProgrammaticChange = React.useRef(false);

  // Create local ref to track which version we're currently editing (humanized or original)
  const isEditingHumanizedContent = React.useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Note: Scroll detection no longer needed since unlock card is embedded in content

  // Debounce the save content function
  const debouncedSaveContent = useDebounce((content: string, options = {}) => {
    handleSaveContent(content, options);
  }, 1000);
  const turndownService = setupTurndownService();
  const editor = useEditor(
    {
      extensions: getEditorExtensions(),
      onCreate({ editor }) {
        editor.view.dom.addEventListener(
          "paste",
          (event) => {
            if (document.activeElement?.closest(".MuiTooltip-popper")) {
              event.stopPropagation();
            }
          },
          true
        );
        lastSavedContent.current = editor.getHTML();
        // Initialize word count
        setWordCount(getWordCount(editor.getText()));
      },
      content: editorContent,
      onUpdate: ({ editor }) => {
        if (isProgrammaticChange.current) {
          return;
        }
        const currentContent = editor.getHTML();

        // Update word count
        setWordCount(getWordCount(editor.getText()));

        if (currentContent !== lastSavedContent.current) {
          // Use our local ref to track which version we're saving
          const saveOptions = {
            isEdited: true,
            isHumanized: isEditingHumanizedContent.current ?? false,
          };

          // Debounce the save operation to avoid excessive API calls
          debouncedSaveContent(currentContent, saveOptions);
          lastSavedContent.current = currentContent;
        }
      },
    },
    [editorContent]
  );

  // Function to load content
  const loadContent = async () => {
    try {
      setIsLoading(true);
      const data = await fetchContent(id);

      // Process the content based on the data format returned
      if (data) {
        const contentHtml = converter.makeHtml(data.content || "");
        const humanizedHtml = converter.makeHtml(data.humanizedContent || "");

        setHtml(contentHtml);
        setHumanizedHtml(humanizedHtml);

        // Set both states based on the data
        dispatch(setIsHumanized(data.isHumanized));
        dispatch(setShowHumanizeContent(data.isHumanized));

        // Initialize editing state
        isEditingHumanizedContent.current = data.isHumanized;
        setIsEditingHumanizedVersion(data.isHumanized);

        // Set the initial content
        const initialContent = data.isHumanized ? humanizedHtml : contentHtml;

        // Initialize word count from HTML content (which will be displayed in editor)
        // Create a temporary div to extract text from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = initialContent;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';
        const initialWordCount = getWordCount(textContent);
        setWordCount(initialWordCount);

        // Set the initial content - truncation will be handled in the truncation effect
        setEditorContent(initialContent);
        handleSaveEvaluationData(data);

        // Set the article link
        if (data.articleLink) {
          setArticleLink(data.articleLink);
        }
      }
    } catch (error) {
      dispatch(
        snackAdded({
          message: `Failed to load content`,
          type: "error",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEvaluationData = async (data: any) => {
    // Process and validate the evaluationData
    try {
      let isValid = false;

      if (data.articleEEATScore) {
        try {
          // Get articles and analysis
          let articles = data.articleEEATScore.articles;
          let analysis = data.articleEEATScore.analysis;
          let suggestions = data.articleEEATScore.suggestions;

          // Parse if needed
          const parsedArticles =
            typeof articles === "string" ? JSON.parse(articles) : articles;
          const parsedAnalysis =
            typeof analysis === "string" ? JSON.parse(analysis) : analysis;

          // Check for required structure in articles
          if (
            !parsedArticles?.original?.scores ||
            !parsedArticles?.updated?.scores ||
            !parsedArticles?.original?.overall ||
            !parsedArticles?.updated?.overall
          ) {
            throw new Error("Articles data is missing required structure");
          }

          // Verify scores contain all required metrics
          const originalScores = parsedArticles.original.scores;
          const updatedScores = parsedArticles.updated.scores;

          if (
            typeof originalScores.experience !== "number" ||
            typeof originalScores.expertise !== "number" ||
            typeof originalScores.authoritativeness !== "number" ||
            typeof originalScores.trustworthiness !== "number"
          ) {
            throw new Error("Original scores are missing required metrics");
          }

          if (
            typeof updatedScores.experience !== "number" ||
            typeof updatedScores.expertise !== "number" ||
            typeof updatedScores.authoritativeness !== "number" ||
            typeof updatedScores.trustworthiness !== "number"
          ) {
            throw new Error("Updated scores are missing required metrics");
          }

          // Check analysis structure
          if (
            !parsedAnalysis?.experience?.comparison ||
            !parsedAnalysis?.expertise?.comparison ||
            !parsedAnalysis?.authoritativeness?.comparison ||
            !parsedAnalysis?.trustworthiness?.comparison ||
            !Array.isArray(parsedAnalysis?.summary)
          ) {
            throw new Error("Analysis is missing required structure");
          }

          // If we reach here without error, data is valid
          isValid = true;
        } catch (error) {
          console.error("Invalid EEAT score data:", error);
          isValid = false;
        }
      } else {
        isValid = true;
      }

      setEvaluationData({ articleEEATScore: data.articleEEATScore });
      setIsValidEvaluationData(isValid);
    } catch (error) {
      console.error("Failed to save evaluation data:", error);
    }
  };

  function convertToMarkdown(content: string) {
    // Add color span rule
    turndownService.addRule("colorSpan", {
      filter: function (node) {
        return node.nodeName === "SPAN" && node.style && node.style.color;
      },
      replacement: function (content, node) {
        const color = node.style.color;
        return `<span style="color: ${color};">${content}</span>`;
      },
    });

    return turndownService.turndown(content);
  }
  // Function to save content
  const handleSaveContent = async (content: string, options: any) => {
    // Mark that we are currently saving so other updates can be deferred
    isContentSaving.current = true;

    const markdownContent = convertToMarkdown(content);

    // Calculate character count from markdown content
    const charCount = markdownContent.length;

    try {
      const result = await saveContent(markdownContent, { ...options, charCount });

      // After saving, update the appropriate HTML state based on what was saved
      if (options.isHumanized) {
        setHumanizedHtml(content);
      } else {
        setHtml(content);
      }

      // Update the reference of the last successfully saved content
      lastSavedContent.current = content;

      return result;
    } catch (error) {
      dispatch(
        snackAdded({
          message: "Failed to save changes",
          type: "error",
        })
      );
      throw error;
    } finally {
      // Reset the saving flag regardless of success or failure
      isContentSaving.current = false;
    }
  };

  // Load content when component mounts or ID changes
  useEffect(() => {
    if (id) {
      loadContent();
    }
  }, [id]);

  // Handle content truncation for preview mode
  useEffect(() => {
    if (!editor || !hideArticle) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Wait for content to be loaded
    if (!html || html.length === 0) {
      return;
    }

    const content = editor.getHTML();

    if (content.length < 100) {
      setUseScrollBasedTruncation(false);
      return;
    }

    // Use TipTap commands to insert unlock card after 4 paragraphs
    setUseScrollBasedTruncation(true);

    isProgrammaticChange.current = true;
    insertUnlockCardWithTipTap(editor, content, isOptimizedMode);
    isProgrammaticChange.current = false;
    setTruncatedContent(content);
  }, [hideArticle, html, editor]);


  // Helper functions
  const selectedWords = () => {
    return countSelectedWords(editor);
  };

  const handleAddMetaToArticle = (html: string) => {
    return addMetaToArticle(html, title, description);
  };

  const handleDownload = () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    let htmlToDownload = editor?.getHTML();
    const htmlWithMeta = handleAddMetaToArticle(htmlToDownload);
    Export2Docx(htmlWithMeta, title);

    // Reset download flag if using the optimized editor
    if (additionalProps.setIsDownloadContent) {
      additionalProps.setIsDownloadContent(false);
    }
  };

  const handleCopy = async () => {
    if (hideArticle) {
      setShowPaymentModal(true);
      return;
    }

    try {
      let htmlToCopy = editor?.getHTML();
      const htmlWithMeta = handleAddMetaToArticle(htmlToCopy);

      const success = await copyHtmlToClipboard(htmlWithMeta);
      if (success) {
        dispatch(
          snackAdded({
            message: "Content copied to clipboard!",
            type: "success",
            duration: 2000,
          })
        );
      } else {
        throw new Error("Clipboard copy failed");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      dispatch(
        snackAdded({
          message: "Failed to copy content. Please try again.",
          type: "error",
          duration: 1000,
        })
      );
    }

    // Reset copy flag if using the optimized editor
    if (additionalProps.setIsCopyContent) {
      additionalProps.setIsCopyContent(false);
    }
  };

  const handleAIDropdown = () => {
    if (isEEATMode) {
      dispatch(
        snackAdded({
          message:
            "You can't apply AI to an EEAT article. Please use Update Article to improve your article.",
          type: "info",
        })
      );
      return;
    }
    isProgrammaticChange.current = true;
    const { from, to } = editor.state.selection;
    editor
      ?.chain()
      .setTextSelection({ from, to })
      .focus()
      .setHighlight("lightgray")
      .run();
    isProgrammaticChange.current = false;

    let startCoords = editor.view.coordsAtPos(from);
    let endCoords = editor.view.coordsAtPos(to);

    // Set coordinates for the dropdown
    // Position it slightly below and to the left of the selected text
    // Use the end of the selection (where the cursor would be) as the anchor point
    setCords({
      top: endCoords.bottom + 10, // 10px below the selection
      left: endCoords.left - 20, // 20px to the left of the end of selection
      bottom: endCoords.bottom,
      right: endCoords.right,
    });

    if (!scrollContainerRef.current) {
      return;
    }

    // Find all possible scroll sources and use the maximum value
    const editorDOM = editor.view.dom;

    // Get all possible scroll values
    const windowScrollY =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;
    const containerScrollTop = scrollContainerRef.current.scrollTop || 0;
    const editorScrollTop = editorDOM.scrollTop || 0;

    // Check for scrollable parent elements
    let parentScrollTop = 0;
    let currentElement = editorDOM.parentElement;
    let scrollableParent = null;

    while (currentElement && currentElement !== document.body) {
      if (
        currentElement.scrollHeight > currentElement.clientHeight &&
        currentElement.scrollTop > 0
      ) {
        scrollableParent = currentElement;
        parentScrollTop = currentElement.scrollTop;

        break;
      }
      currentElement = currentElement.parentElement;
    }

    // Use the maximum scroll value from any source
    const scrollY = Math.max(
      windowScrollY,
      containerScrollTop,
      editorScrollTop,
      parentScrollTop
    );

    // Store the effective scroll position for use in dropdown positioning
    const effectiveScrollPosition = scrollY;

    // Calculate position relative to the editor's position in the container
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const editorRect = editor.view.dom.getBoundingClientRect();

    // We will position the dropdown at the beginning of the last line of selected text
    // Use a safer approach to get text content
    const selectionText = editor.state.doc.textBetween(from, to);

    // Find the last newline in the selection
    const lastNewlineIndex = selectionText.lastIndexOf("\n");

    // Position at the start of the last line
    let lastLineStartPos = from;
    if (lastNewlineIndex !== -1) {
      // Found a newline, position after it (first character of last line)
      lastLineStartPos = from + lastNewlineIndex + 1;
    }

    // Get coordinates for the start of the last line
    let startOfLastLineCoords = editor.view.coordsAtPos(lastLineStartPos);

    // Calculate available space below the selection
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - endCoords.bottom;

    // Height of the dropdown (approximate)
    const dropdownHeight = 150; // Adjust based on your dropdown's actual height

    // Check if there's enough space below
    const positionAbove = spaceBelow < dropdownHeight;

    let relativeTop, relativeLeft;

    if (positionAbove) {
      // Position above the selection
      relativeTop =
        startOfLastLineCoords.top -
        containerRect.top +
        scrollY -
        dropdownHeight;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    } else {
      // Position below the selection (original behavior)
      relativeTop = startOfLastLineCoords.top - containerRect.top + scrollY;
      relativeLeft = startOfLastLineCoords.left - containerRect.left;
    }

    // Set coordinates for the dropdown
    setCords({
      top: positionAbove ? relativeTop : relativeTop + 10, // If above, no extra padding
      left: relativeLeft,
      bottom: relativeTop,
      right: endCoords.right - containerRect.left,
    });

    // Extract selected blocks
    const selectedBlocks = extractSelectedBlocks(
      editor,
      editor.state.selection.from,
      editor.state.selection.to,
      converter
    );

    if (selectedWords() > 500) {
      dispatch(
        snackAdded({
          message: "Please select less than 500 words",
          type: "error",
        })
      );
      editor?.commands.unsetHighlight();
      return;
    }

    // Set state for dropdown
    setShowAIDropdown(true);
    setShouldShowBubbleMenu(false);
    setSelectedText(selectedBlocks);

    // Focus and scroll to the element
    setTimeout(() => {
      try {
        // Try direct window scrolling as a backup
        const editorRect = scrollContainerRef.current
          ?.querySelector(".ProseMirror")
          ?.getBoundingClientRect();
        if (editorRect) {
          // Use a simpler approach - just scroll to a fixed offset from the selection
          // This is more reliable across different scroll positions
          const targetScroll = Math.max(0, relativeTop - 150);

          // Find the scrollable container
          let scrollContainer = null;
          let currentEl = editorDOM;

          while (currentEl && currentEl !== document.body) {
            if (currentEl.scrollHeight > currentEl.clientHeight) {
              scrollContainer = currentEl;
              break;
            }
            currentEl = currentEl.parentElement;
          }

          // If we found a scrollable container, scroll it
          if (scrollContainer) {
            scrollContainer.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          } else {
            window.scrollTo({
              top: targetScroll,
              behavior: "smooth",
            });
          }
        }
      } catch (error) {
        console.error("Error during focus/scroll:", error);
      }
    }, 100);
  };

  const handleCloseAIDropdown = () => {
    setShowAIDropdown(false);
    setShouldShowBubbleMenu(true);
    setSelectedText([]);
    setCords({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    });

    // Remove the highlight
    editor?.commands.unsetHighlight();
    setBottomMargin(0);
  };

  const unlockArticle = () => {
    setShowPaymentModal(true);

    // Reset flags if using the regular editor
    if (!isOptimizedMode) {
      if (additionalProps.setIsDownloadContent) {
        additionalProps.setIsDownloadContent(false);
      }
      if (additionalProps.setIsCopyContent) {
        additionalProps.setIsCopyContent(false);
      }
    }
  };

  // Effect to handle unlock events from the custom TipTap extension
  useEffect(() => {
    if (!hideArticle || !useScrollBasedTruncation) {
      return;
    }

    const handleUnlockEvent = (event: CustomEvent) => {
      event.preventDefault();
      unlockArticle();
    };

    // Listen for the custom unlock event
    document.addEventListener('unlockArticle', handleUnlockEvent as EventListener);

    return () => {
      document.removeEventListener('unlockArticle', handleUnlockEvent as EventListener);
    };
  }, [hideArticle, useScrollBasedTruncation]);

  // Clean up editor on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Effect for copy/download in regular editor mode
  useEffect(() => {
    if (additionalProps.isCopyContent) {
      handleCopy();
    }
  }, [additionalProps.isCopyContent]);

  useEffect(() => {
    if (additionalProps.isDownloadContent) {
      handleDownload();
    }
  }, [additionalProps.isDownloadContent]);

  // Effect to handle content switching
  useEffect(() => {
    if (!editor || !html || !humanizedHtml) return;

    const newContent = additionalProps.showHumanizeContent
      ? humanizedHtml
      : html;
    const currentContent = editor.getHTML();

    if (newContent !== currentContent) {
      // Set flags before changing content
      isProgrammaticChange.current = true;
      isContentSaving.current = true;

      // Update our local tracking of which version we're editing
      isEditingHumanizedContent.current = additionalProps.showHumanizeContent;
      setIsEditingHumanizedVersion(additionalProps.showHumanizeContent);

      // Update the content
      // Apply truncation if article should be hidden
      if (hideArticle && useScrollBasedTruncation) {
        insertUnlockCardWithTipTap(editor, newContent, isOptimizedMode);
      } else {
        editor.commands.setContent(newContent);
      }
      lastSavedContent.current = newContent;

      // Update word count immediately based on the HTML content
      // Use a small delay to ensure editor has processed the content
      setTimeout(() => {
        if (editor) {
          setWordCount(getWordCount(editor.getText()));
        }
      }, 100); // Short delay just to ensure content is rendered

      // Clear flags after content is updated with a longer timeout
      setTimeout(() => {
        isProgrammaticChange.current = false;
        isContentSaving.current = false;
      }, 3000); // Extended timeout to prevent race conditions
    }
  }, [additionalProps.showHumanizeContent, editor, hideArticle, useScrollBasedTruncation, isOptimizedMode]);

  // Add a new effect to track isEditingHumanizedVersion changes separately
  useEffect(() => {}, [isEditingHumanizedVersion]);

  // Add this effect to handle humanization
  useEffect(() => {
    const handleHumanization = async () => {
      // Exit early if not humanizing, no editor, or no humanize function
      if (
        !additionalProps.isHumanizing ||
        !editor ||
        !additionalProps.onHumanize
      )
        return;

      try {
        const content = editor.getHTML();

        // Call the provided humanize function
        // local
        //const url = "http://localhost:8000/api/v1/humanizer/stream";
        // prod
        const url = "https://humanizer.seowind.io/api/v1/humanizer/stream";
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: content,
            source: "ai_writing",
          }),
        });

        if (response.status === 429) {
          const data = await response.json();
          dispatch(
            snackAdded({
              message: data.message,
              type: "error",
              duration: 2000,
            })
          );
          dispatch(endHumanization(false));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder("utf-8");
        let accumulatedText = "";

        while (true) {
          const { value, done } = await reader?.read()!;
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          accumulatedText += chunk;

          const html = converter.makeHtml(accumulatedText);
          isProgrammaticChange.current = true;

          // Apply truncation if article should be hidden
          if (hideArticle && useScrollBasedTruncation) {
            insertUnlockCardWithTipTap(editor, html, isOptimizedMode);
          } else {
            editor.commands.setContent(html);
          }

          isProgrammaticChange.current = false;
        }
        // Save content and update state
        await handleSaveContent(editor.getHTML(), {
          isHumanized: true,
          isEdited: false,
        });

        // Update states in correct order
        dispatch(endHumanization(true));

        // Fetch latest content
        await loadContent();

        // Content will be updated through the normal fetch mechanism
      } catch (error) {
        console.error("Error humanizing content:", error);
        dispatch(
          snackAdded({
            message: "Failed to humanize content",
            type: "error",
            duration: 2000,
          })
        );
      }
    };

    handleHumanization();
  }, [additionalProps.isHumanizing, editor, additionalProps.onHumanize]);

  // Add this effect to handle navigation blocking during humanization
  useEffect(() => {
    // Block navigation while humanizing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (additionalProps.isHumanizing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    // Block Next.js client-side navigation
    const handleRouteChange = (url: string) => {
      if (additionalProps.isHumanizing) {
        // Prevent the default behavior
        window.history.pushState(null, "", window.location.href);
        // Show warning
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
        throw "Navigation cancelled";
      }
    };

    // Handle popstate (browser back/forward buttons)
    const handlePopState = () => {
      if (additionalProps.isHumanizing) {
        // Push current URL back to history to prevent navigation
        window.history.pushState(null, "", window.location.href);
        dispatch(
          snackAdded({
            message: "Please wait for humanization to complete",
            type: "warning",
            duration: 3000,
          })
        );
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    router.events.on("routeChangeStart", handleRouteChange);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [additionalProps.isHumanizing]);

  // Add effect to log when showHumanizeContent changes
  useEffect(() => {}, [additionalProps.showHumanizeContent]);

  const handleToggle = (event) => {
    const isChecked = event.target.checked;
    // Just update the display preference
    dispatch(setShowHumanizeContent(isChecked));
  };

  const checArticleLimit = async () => {
    // Refresh the current plan
    const currentPlan = (await dispatch(getActivePlan())).payload?.plan;
    const numberOfAIWords = Number(currentPlan?.numberOfWordsInPlan || 0);
    const numberOfArticles = Number(currentPlan?.numberOfArticlesInPlan || 0);
    let aiWordsLeft = numberOfAIWords - (currentPlan?.totalWordsWritten || 0);
    let articlesLeft =
      numberOfArticles - (currentPlan?.totalArticlesWritten || 0);
    if (aiWordsLeft < 0) {
      aiWordsLeft = 0;
    }
    if (articlesLeft < 0) {
      articlesLeft = 0;
    }

    if (aiWordsLeft === 0 || articlesLeft === 0) {
      let text = "";
      if (articlesLeft === 0) {
        text = `You've reached a limit for Updating Articles`;
      } else {
        text = ` You've reached a limit of word count`;
      }

      dispatch(aiWordsUpgradeModalShowed(text));
      return true;
    }
    return false;
  };
  const handleUpdateArticleEEAT = async () => {
    setIsOptimizeContentDialogLoading(true);
    setIsOptimizeContentDialogOpen(false);
    const planDetails = (await dispatch(getActivePlan())) as any;
    let plan = planDetails?.payload?.plan ?? undefined;

    if (
      plan === undefined ||
      Object.keys(plan).length === 0 ||
      planDetails?.payload?.error
    ) {
      setShowOnboardingModal(true);
      setIsOptimizeContentDialogLoading(false);
      return;
    }

    const isArticleLimitReached = await checArticleLimit();

    setIsOptimizeContentDialogLoading(false);
    if (!isArticleLimitReached) {
      setIsOptimizeContentDialogOpen(true);
    }
  };
  const handleCloseOptimizeContentDialog = () => {
    setIsOptimizeContentDialogOpen(false);
  };
  const handleOptimizeContentDialogOpen = () => {
    setIsOptimizeContentDialogOpen(true);
  };
  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <AddPaymentModal
        open={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);

          // Reset flags if using the regular editor
          if (!isOptimizedMode) {
            if (additionalProps.setIsDownloadContent) {
              additionalProps.setIsDownloadContent(false);
            }
            if (additionalProps.setIsCopyContent) {
              additionalProps.setIsCopyContent(false);
            }
          }
        }}
        location={""}
      />
      {showOnboardingModal && (
        <Onboarding
          onClose={() => setShowOnboardingModal(false)}
          isEEATMode={isEEATMode}
          articleLink={articleLink}
        />
      )}
      <OptimizeContentDialog
        open={isOptimizeContentDialogOpen}
        limit={false}
        onClose={handleCloseOptimizeContentDialog}
        onCreated={handleOptimizeContentDialogOpen}
        isRedirect={false}
        isShowInputs={false}
        link={articleLink}
      />
      <Box
        sx={{
          width: "100%",
          margin: "auto",
          position: "relative",
          display: "flex",
          gap: 3,
          px: 3,
          height: "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            height: "100%",
            overflow: "auto",
            position: "relative",
          }}
        >
          <Card
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              minHeight: additionalProps.isHumanizing
                ? "calc(100vh - 64px)"
                : "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                top: "3rem",
                left: "36%",
                transform: "translateX(-50%)",
                maxWidth: "70%",
                zIndex: hideArticle || isEEATMode ? 1 : 99999,
              }}
            >
              <MenuBar
                editor={editor}
                handleAidropdown={handleAIDropdown}
                evaluationData={evaluationData}
                isEEATMode={isEEATMode}
              />
            </Box>
            <Editor
              editor={editor}
              isHumanizer={false}
              isLoading={isLoading}
              title={title}
              description={description}
              wordCount={wordCount}
            />

            {/* Render skeletons below the Editor for hiding content - only when not using scroll-based truncation */}
            {hideArticle && !useScrollBasedTruncation && (
              <Box
                sx={{
                  width: "100%",
                  padding: "16px",
                  background: "linear-gradient(to bottom, transparent, white)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Skeleton Loaders */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={20}
                    width={"100%"}
                    animation={false}
                  />
                  <Skeleton
                    variant="text"
                    height={15}
                    width={"65%"}
                    animation={false}
                  />
                </Box>

                {/* Unlock Card */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "65px",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#5048E5",
                      borderRadius: "10px",
                      boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "10px 16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Your {isOptimizedMode ? "optimized " : ""}content is
                          ready
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                          }}
                        >
                          Subscribe to see the full article
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={unlockArticle}
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            color: "#fff",
                            padding: "8px 16px",
                            background: "#EB1F8D !important",
                            width: "150px",
                            height: "49px",
                            borderRadius: "8px",
                          }}
                        >
                          Unlock Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}

            <BubbleMenu
              editor={editor}
              className="bubble-menu"
              tippyOptions={{
                duration: 100,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  width: "120px",
                  height: "40px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: 400,
                }}
                onClick={handleAIDropdown}
              >
                <MagicWandIcon sx={{ mt: 1 }} />
                Ask AI
              </Button>
            </BubbleMenu>

            {showAIDropdown && (
              <RegenerateDropdown
                startCord={cords.top}
                endCord={cords.left}
                bottomCord={cords.bottom}
                rightCord={cords.right}
                open={showAIDropdown}
                selectedText={selectedText}
                editor={editor}
                onClose={handleCloseAIDropdown}
                setMarginBottom={(margin) => setBottomMargin(margin)}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </Card>
        </Box>
        {/* Sidebar */}

        <Box sx={{ width: "384px", height: "10%" }}>
          <Sidebar
            title={
              isOptimizedMode ? "EEAT Score Comparison" : "Article EEAT Score"
            }
            recommendedScore={8.0}
            evaluationData={evaluationData}
            optimizedMode={isOptimizedMode}
            isLoading={isLoading}
            isEEATMode={isEEATMode}
            onUpdateArticleEEAT={handleUpdateArticleEEAT}
            isEEATLoading={isOptimizeContentDialogLoading}
          />
        </Box>
      </Box>
    </>
  );
};

export default ArticleEditorComponent;
